import React, { useEffect, useRef, useState } from 'react';

/**
 * React adapter over the shared LkEditor engine (classic-editor.js) — the same
 * engine <x-backend.ckeditor> uses in Blade, so presets/plugins/license live in
 * one place. Loads its own assets (CDN UMD + /js/ckeditor-classic.js) lazily,
 * once per page, so any React surface can drop this in with zero Blade setup.
 *
 * Uncontrolled: `value` is the initial content only; edits flow out through
 * `onChange(html)`. To set content after mount, use the instance from
 * `onReady(editor)` and call editor.setData().
 */

// Keep in sync with the CDN version in <x-backend.ckeditor> (ckeditor.blade.php).
const CDN_VERSION = '44.3.0';

let assetsPromise = null;

function loadScript(src) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.crossOrigin = 'anonymous';
        script.onload = resolve;
        script.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(script);
    });
}

function loadEditorAssets() {
    if (assetsPromise) return assetsPromise;

    assetsPromise = (async () => {
        if (!document.querySelector('link[data-lk-ckeditor]')) {
            const css = document.createElement('link');
            css.rel = 'stylesheet';
            css.href = `https://cdn.ckeditor.com/ckeditor5/${CDN_VERSION}/ckeditor5.css`;
            css.crossOrigin = 'anonymous';
            css.setAttribute('data-lk-ckeditor', '');
            document.head.appendChild(css);
        }

        if (!window.CKEDITOR) {
            await loadScript(`https://cdn.ckeditor.com/ckeditor5/${CDN_VERSION}/ckeditor5.umd.js`);
        }

        if (!window.LkEditor) {
            await loadScript('/js/ckeditor-classic.js');
        }
    })();

    return assetsPromise;
}

export default function CkEditorField({
    value = '',
    preset = 'content',
    licenseKey = null,
    uploadUrl = '/upload-image',
    uploadHeaders = null,
    placeholder = '',
    maxWidth = '830px',
    onChange,
    onReady,
}) {
    const hostRef = useRef(null);
    const editorRef = useRef(null);
    // Boot takes a moment (CDN assets + init) — the source textarea stays
    // hidden and a spinner holds the space, so raw HTML never flashes.
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);
    // Latest callbacks reachable from the mount-once effect.
    const onChangeRef = useRef(onChange);
    const onReadyRef = useRef(onReady);
    onChangeRef.current = onChange;
    onReadyRef.current = onReady;

    useEffect(() => {
        let disposed = false;

        loadEditorAssets()
            .then(() => {
                if (disposed || !hostRef.current) return;

                window.LkEditor.create(hostRef.current, {
                    preset,
                    licenseKey,
                    uploadUrl,
                    uploadHeaders,
                    placeholder,
                    onReady: (editor) => {
                        if (disposed) {
                            editor.destroy().catch(() => {});
                            return;
                        }
                        editorRef.current = editor;
                        setReady(true);
                        editor.model.document.on('change:data', () => {
                            if (onChangeRef.current) onChangeRef.current(editor.getData());
                        });
                        if (onReadyRef.current) onReadyRef.current(editor);
                    },
                });
            })
            .catch((error) => {
                console.error('CkEditorField:', error);
                if (!disposed) setFailed(true);
            });

        return () => {
            disposed = true;
            if (editorRef.current) {
                editorRef.current.destroy().catch(() => {});
                editorRef.current = null;
            }
        };
        // Mounts once by design — the editor owns this DOM node afterwards.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="lk-ckeditor" style={maxWidth ? { maxWidth } : undefined}>
            {!ready && (
                <div className="lk-ckeditor__loading">
                    {failed
                        ? 'The editor failed to load — refresh the page to try again.'
                        : (<><span className="lk-ckeditor__spinner" /> Loading editor…</>)}
                </div>
            )}
            {/* Hidden from the start: ClassicEditor reads its value and renders
                its own UI beside it, so it never needs to be visible. */}
            <textarea ref={hostRef} defaultValue={value} rows={10} className="form-control" style={{ display: 'none' }} />
        </div>
    );
}
